import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, TextField, Button, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import logo from '../../assets/VisibeenLogo.png';
import InstagramIcon from '../Dashboard/icons/InstagramIcon';
import FacebookIcon from '../icons/FacebookIcon';
import LinkedInIcon from '../icons/LinkedInIcon';
import YouTubeIcon from '../Dashboard/icons/YouTubeIcon';

const FooterContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(93.12deg, rgba(11,145,214,1) 0%, rgba(239,35,42,1) 100%)',
  padding: '40px 0',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.down('md')]: {
    padding: '32px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '24px 0'
  }
}));

const FooterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 40px',
  gap: '125px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    padding: '0 24px',
    gap: '40px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    gap: '32px'
  }
}));

const FooterLeft = styled(Stack)(({ theme }) => ({
  gap: '40px',
  alignItems: 'flex-start'
}));

const FooterLogo = styled('img')(({ theme }) => ({
  width: '143px',
  height: '48px'
}));

const CopyrightText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#ffffff',
  lineHeight: '20px'
}));

const SocialIcons = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '16px'
}));

const SocialIconLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  color: '#ffffff',
  textDecoration: 'none',
  '&:hover': {
    opacity: 0.8
  }
}));

const FooterColumns = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '30px',
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '24px',
    width: '100%'
  }
}));

const FooterColumn = styled(Stack)(({ theme }) => ({
  gap: '24px',
  minWidth: '160px'
}));

const ColumnTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: '#ffffff'
}));

const ColumnLinks = styled(Stack)(({ theme }) => ({
  gap: '12px'
}));

const ColumnLink = styled(Link)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#ffffff',
  textDecoration: 'none',
  '&:hover': {
    opacity: 0.8
  }
}));

const NewsletterContainer = styled(Stack)(({ theme }) => ({
  gap: '16px',
  minWidth: '320px',
  [theme.breakpoints.down('md')]: {
    minWidth: '100%',
    width: '100%'
  }
}));

const NewsletterDescription = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.8)',
  lineHeight: '20px'
}));

const NewsletterForm = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%'
}));

const NewsletterInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: '48px',
  padding: '12px 48px 12px 16px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#ffffff',
  backgroundColor: 'transparent !important',
  border: 'none',
  borderRadius: '4px',
  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.3)',
  outline: 'none',
  transition: 'all 0.2s ease',
  '&::placeholder': {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '14px'
  },
  '&:focus': {
    backgroundColor: 'transparent !important',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.5)'
  },
  '&:hover': {
    backgroundColor: 'transparent !important',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.4)'
  },
  '&:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
    WebkitTextFillColor: '#ffffff !important',
    transition: 'background-color 5000s ease-in-out 0s'
  },
  '&:-webkit-autofill:hover': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
    WebkitTextFillColor: '#ffffff !important'
  },
  '&:-webkit-autofill:focus': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
    WebkitTextFillColor: '#ffffff !important'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    height: '44px',
    padding: '10px 44px 10px 14px'
  }
}));

const SendButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  right: '4px',
  top: '50%',
  transform: 'translateY(-50%)',
  minWidth: '40px',
  width: '40px',
  height: '40px',
  padding: 0,
  backgroundColor: 'transparent',
  color: '#ffffff',
  fontSize: '18px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.8
  },
  '&:active': {
    backgroundColor: 'transparent',
    opacity: 0.6
  },
  [theme.breakpoints.down('sm')]: {
    width: '36px',
    height: '36px',
    minWidth: '36px',
    fontSize: '16px'
  }
}));

const Footer = () => {
  const navigate = useNavigate();

  const handleQuickTutorial = (e) => {
    e.preventDefault();
    navigate('/client-onboarding');
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterLeft>
          <FooterLogo src={logo} alt="Visibeen" />
          <Stack spacing={1}>
            <CopyrightText>Copyright © 2025 e2e digitech pvt ltd.</CopyrightText>
            <CopyrightText>All rights reserved</CopyrightText>
          </Stack>
          <SocialIcons>
            <SocialIconLink href="https://www.instagram.com/visibeen/" target="_blank" rel="noopener noreferrer">
              <InstagramIcon width={32} height={32} color="#ffffff" />
            </SocialIconLink>
            <SocialIconLink href="https://www.facebook.com/profile.php?id=61566867428338" target="_blank" rel="noopener noreferrer">
              <FacebookIcon width={32} height={32} color="#ffffff" />
            </SocialIconLink>
            <SocialIconLink href="https://www.youtube.com/@Visibeen" target="_blank" rel="noopener noreferrer">
              <YouTubeIcon width={32} height={32} color="#ffffff" />
            </SocialIconLink>
            <SocialIconLink href="https://www.linkedin.com/company/visibeen/" target="_blank" rel="noopener noreferrer">
              <LinkedInIcon width={32} height={32} color="#ffffff" />
            </SocialIconLink>
          </SocialIcons>
        </FooterLeft>

        <FooterColumns>
          <FooterColumn>
            <ColumnTitle>Company</ColumnTitle>
            <ColumnLinks>
              <ColumnLink href="/about-us">About us</ColumnLink>
              <ColumnLink href="#" onClick={handleQuickTutorial} style={{ cursor: 'pointer' }}>Quick Tutorial</ColumnLink>
              <ColumnLink href="/pricing">Pricing</ColumnLink>
            </ColumnLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Support</ColumnTitle>
            <ColumnLinks>
              <ColumnLink href="mailto:support@visibeen.com">support@visibeen.com</ColumnLink>
              <ColumnLink href="/contact-us">Help center</ColumnLink>
              <ColumnLink href="/terms-of-service">Terms of service</ColumnLink>
              <ColumnLink href="/privacy-policy">Privacy policy</ColumnLink>
              <ColumnLink href="/disclaimer">Disclaimer</ColumnLink>
            </ColumnLinks>
          </FooterColumn>

          <NewsletterContainer>
            <ColumnTitle>Stay up to date</ColumnTitle>
            <NewsletterDescription>
              Subscribe to our newsletter for the latest updates and exclusive offers.
            </NewsletterDescription>
            <NewsletterForm>
              <NewsletterInput
                type="email"
                placeholder="Enter your email address"
                aria-label="Email address"
              />
              <SendButton aria-label="Subscribe">
                →
              </SendButton>
            </NewsletterForm>
          </NewsletterContainer>
        </FooterColumns>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;